import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { appConfig } from '../../config/app';
import { sharedConfig } from '../../config/shared';

const Privacy = () => {
  return (
    <div className="min-vh-100 py-5 legal-page-container">
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} xl={10}>
            {/* Header */}
            <div className="text-center mb-5">
              <h1 className="display-4 fw-bold text-white mb-3">Privacy Policy</h1>
              <p className="lead text-light">
                Last updated: September 5, 2025
              </p>
            </div>

            {/* Main Content */}
            <Card className="glass-card border-0 shadow-lg legal-page-card">
              <Card.Body className="p-4 p-lg-5">
                <div className="legal-content">
                  {/* Introduction */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">1. Introduction & Scope</h2>
                    <p className="text-light">
                      This Privacy Policy describes how {sharedConfig.company.companyName} DBA {sharedConfig.brand.name} ("we," "us," or "our") 
                      collects, uses, and protects your information when you use our fantasy football recap video service. 
                      By using our service, you agree to the collection and use of information in accordance with this policy.
                    </p>
                  </section>

                  {/* Information We Collect */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">2. Information We Collect</h2>
                    <p className="text-light mb-3">We collect the following types of information:</p>
                    <ul className="text-light">
                      <li><strong>Account Information:</strong> Name, email address, and fantasy football platform usernames</li>
                      <li><strong>Fantasy Football Data:</strong> League information, team data, and performance statistics from your fantasy platform</li>
                      <li><strong>Payment Information:</strong> Billing details and payment method information (processed securely by our payment processor)</li>
                      <li><strong>Usage Data:</strong> How you interact with our service, including video views and feature usage</li>
                      <li><strong>Communication Data:</strong> Emails, support requests, and other communications with us</li>
                    </ul>
                  </section>

                  {/* How We Use Your Information */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">3. How We Use Your Information</h2>
                    <p className="text-light mb-3">We use your information to:</p>
                    <ul className="text-light">
                      <li>Provide and maintain our fantasy football recap video service</li>
                      <li>Generate personalized weekly recap videos for your league</li>
                      <li>Process payments and manage your subscription</li>
                      <li>Send important service updates and communications</li>
                      <li>Provide customer support and respond to inquiries</li>
                      <li>Improve our service and develop new features</li>
                      <li>Ensure compliance with legal obligations</li>
                    </ul>
                  </section>

                  {/* Information Sharing & Disclosure */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">4. Information Sharing & Disclosure</h2>
                    <p className="text-light mb-3">We may share your information in the following circumstances:</p>
                    <ul className="text-light">
                      <li><strong>Service Providers:</strong> With third-party services for email delivery, payment processing, and analytics</li>
                      <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                      <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                      <li><strong>Consent:</strong> When you explicitly consent to sharing</li>
                    </ul>
                    <p className="text-light">
                      We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                    </p>
                  </section>

                  {/* Data Security & Retention */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">5. Data Security & Retention</h2>
                    <p className="text-light mb-3">
                      We implement appropriate security measures to protect your information, including:
                    </p>
                    <ul className="text-light">
                      <li>Encryption of data in transit and at rest</li>
                      <li>Regular security assessments and updates</li>
                      <li>Access controls and authentication measures</li>
                      <li>Secure data centers and infrastructure</li>
                    </ul>
                    <p className="text-light">
                      We retain your information for as long as necessary to provide our services and comply with legal obligations. 
                      You may request deletion of your data at any time.
                    </p>
                  </section>

                  {/* Your Rights & Choices */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">6. Your Rights & Choices</h2>
                    <p className="text-light mb-3">You have the following rights regarding your information:</p>
                    <ul className="text-light">
                      <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                      <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                      <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                      <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                      <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                    </ul>
                    <p className="text-light">
                      To exercise these rights, please contact us at {appConfig.contact.primary.email}.
                    </p>
                  </section>

                  {/* Children's Privacy */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">7. Children's Privacy</h2>
                    <p className="text-light">
                      Our service is designed for users who are legally eligible to use the product in the United States 
                      and who are at least 18 years old. We do not knowingly collect personal information from children 
                      under 18. If you believe we have collected information from a child under 18, please contact us 
                      immediately so we can take appropriate action.
                    </p>
                  </section>

                  {/* International Users */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">8. International Users</h2>
                    <p className="text-light">
                      International users are allowed to use our product as long as it is legal in their country. 
                      We make no representations as to the legal status of our service in any country, and we will 
                      do our best to comply with all applicable laws. By using our service, you acknowledge that 
                      your information may be transferred to and processed in the United States, where our servers 
                      are located and our central database is operated.
                    </p>
                  </section>

                  {/* Changes to This Policy */}
                  <section className="mb-5">
                    <h2 className="h3 text-white mb-3">9. Changes to This Policy</h2>
                    <p className="text-light">
                      We may update this Privacy Policy from time to time. We will notify you of any material changes 
                      by posting the new policy on this page and updating the "Last updated" date. Your continued use 
                      of our service after any changes constitutes acceptance of the updated policy.
                    </p>
                  </section>

                  {/* Contact Us */}
                  <section className="mb-0">
                    <h2 className="h3 text-white mb-3">10. Contact Us</h2>
                    <p className="text-light">
                      If you have any questions about this Privacy Policy or our privacy practices, please contact us:
                    </p>
                    <div className="bg-light bg-opacity-10 p-3 rounded">
                      <p className="text-light mb-1">
                        <strong>Email:</strong> {appConfig.contact.primary.email}
                      </p>
                      <p className="text-light mb-1">
                        <strong>Company:</strong> {sharedConfig.company.companyName} DBA {sharedConfig.brand.name}
                      </p>
                      <p className="text-light mb-0">
                        <strong>Website:</strong> {sharedConfig.brand.websiteUrl}
                      </p>
                    </div>
                  </section>
                </div>
              </Card.Body>
            </Card>

            {/* Back to Home Link */}
            <div className="text-center mt-4">
              <Link to="/" className="btn btn-outline-light">
                ← Back to Home
              </Link>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Privacy;

